/**
 * "Is this the same problem?" (8 Oct) — shown instead of filing, when someone
 * else already has an open ticket about the same shared system.
 *
 * Only what the server sends is shown: a short, scrubbed subject, the status,
 * the owning team, how long ago it was raised and the number. Never anyone's
 * name or description, and no link: the ticket is someone else's, and they
 * cannot open it.
 *
 * "Yes, add me to it" raises nothing: the person is noted on that ticket as
 * also affected, and the owner shows `SimilarJoinedNotice`. "No, mine is
 * different" files their own, which tells IT which ticket it is not.
 */

import { Alert, Button, Typography } from "@norican/react";
import { useId } from "react";

import { useT } from "@/i18n";

import type { SimilarTicket } from "../api/contract";
import { useStatusLabel } from "./ticket-badges";

export interface SimilarTicketPromptProps {
  matches: readonly SimilarTicket[];
  /** The ticket being joined, while "add me" is in flight. */
  joiningId: string | null;
  /** "Add me" failed: say so, and leave "No, mine is different" to raise their own. */
  joinFailed: boolean;
  /** Filing their own is in flight. */
  filing: boolean;
  onJoin: (match: SimilarTicket) => void;
  onDifferent: () => void;
}

/** "raised 3 days ago" / "raised today", in the person's language. */
function useRaised() {
  const { t } = useT("tickets");
  return (days: number) =>
    days <= 0 ? t("similar.raisedToday") : t("similar.raisedDaysAgo", { count: days });
}

/** One open ticket, as a sentence: “EPLAN licence server down” (Active, BIS team, raised 2 days ago). */
export function useSimilarLine() {
  const { t } = useT("tickets");
  const statusLabel = useStatusLabel();
  const raised = useRaised();
  return (match: SimilarTicket) => {
    const status =
      match.status.code === "UNKNOWN" && match.status.provider_status.length > 0
        ? match.status.provider_status
        : statusLabel(match.status.code, match.kind);
    const params = {
      subject: match.subject,
      status,
      team: match.owner_team ?? "",
      raised: raised(match.age_days),
    };
    return match.owner_team ? t("similar.line", params) : t("similar.lineNoTeam", params);
  };
}

export function SimilarTicketPrompt({
  matches,
  joiningId,
  joinFailed,
  filing,
  onJoin,
  onDifferent,
}: SimilarTicketPromptProps) {
  const { t } = useT("tickets");
  const headingId = useId();
  const line = useSimilarLine();
  const busy = joiningId !== null || filing;
  const one = matches.length === 1;

  return (
    <section
      aria-labelledby={headingId}
      className="flex flex-col gap-4"
      data-review-keep
      data-similar-prompt
    >
      <Alert tone="info" title={<span id={headingId}>{t("similar.title")}</span>}>
        {one ? t("similar.one") : t("similar.many")}
      </Alert>

      <ul className="flex flex-col gap-3">
        {matches.map((match) => (
          <li
            key={match.ticket_id}
            className="border-border-subtle flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="flex min-w-0 flex-col gap-0.5">
              <Typography variant="body-sm" className="break-words">
                {line(match)}
              </Typography>
              <Typography variant="caption" tone="muted">
                {t("similar.number", { number: match.ticket_number })}
              </Typography>
            </div>
            <Button
              type="button"
              onClick={() => onJoin(match)}
              loading={joiningId === match.ticket_id}
              disabled={busy}
              className="shrink-0"
            >
              {joiningId === match.ticket_id ? t("similar.joining") : t("similar.join")}
            </Button>
          </li>
        ))}
      </ul>

      <Typography variant="body-sm">
        {one ? t("similar.question") : t("similar.questionMany")}
      </Typography>

      {joinFailed ? <Alert tone="warning">{t("similar.joinFailed")}</Alert> : null}

      <div className="flex flex-wrap justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={onDifferent}
          loading={filing}
          disabled={busy}
        >
          {t("similar.different")}
        </Button>
      </div>
    </section>
  );
}

/** What "add me" joined, for the confirmation. */
export interface JoinedTicket {
  number: string;
  subject: string;
}

/** "You've been added" — no new ticket, and why it is not in My tickets. */
export function SimilarJoinedNotice({ joined }: { joined: JoinedTicket }) {
  const { t } = useT("tickets");
  return (
    <Alert tone="success" title={t("similar.joinedTitle")}>
      {t("similar.joined", { subject: joined.subject, number: joined.number })}{" "}
      {t("similar.joinedNotInList")}
    </Alert>
  );
}
